// VaccineRequisitionReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import VaccineRequisitionChart from "./VaccineRequisitionChart";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/THSarabunNew.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const getStatusText = (status) => {
  switch (status) {
    case "Draft":
      return "ร่างใบเบิก";
    case "Pending":
      return "รออนุมัติ";
    case "Approved":
      return "รอรับเข้าคลัง";
    case "Completed":
      return "รับเข้าคลังแล้ว";
    case "Cancelled":
      return "ยกเลิกใบเบิก";
    case "ReceiveRejected":
      return "คลังปลายทางปฏิเสธการรับ";
    default:
      return status || "";
  }
};

// รายการสถานะสำหรับตัวเลือก
const statusOptions = [
  { value: "Draft", label: "ร่างใบเบิก" },
  { value: "Pending", label: "รออนุมัติ" },
  { value: "Approved", label: "รอรับเข้าคลัง" },
  { value: "Completed", label: "รับเข้าคลังแล้ว" },
  { value: "Cancelled", label: "ยกเลิกใบเบิก" },
];

const VaccineRequisitionReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: null,
    EndDate: null,
    FromWarehouseID: "",
    ToWarehouseID: "",
    VaccineID: "",
    Status: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [fromWarehouseOptions, setFromWarehouseOptions] = useState([]);
  const [toWarehouseOptions, setToWarehouseOptions] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [flatData, setFlatData] = useState([]);
  const [requisitionData, setRequisitionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล From Warehouse List
  const fetchFromWarehouseOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const options = response.VaccineWarehousesList || [];
        setFromWarehouseOptions(options);
        if (options.length === 1) {
          const defaultID = options[0].WarehouseID;
          setFilterData((prev) => ({ ...prev, FromWarehouseID: defaultID }));
          fetchToWarehouseOptions(defaultID);
        }
      } else {
        console.error("Failed to fetch from warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching from warehouses:", error);
    }
  };

  // ดึงข้อมูล To Warehouse List based on FromWarehouseID
  const fetchToWarehouseOptions = async (fromID) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineChildWarehousesList",
        data: { ParentWarehouseID: fromID },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setToWarehouseOptions(response.VaccineWarehousesList || []);
      } else {
        console.error("Failed to fetch to warehouses:", response.error);
      }
    } catch (error) {
      console.error("Error fetching to warehouses:", error);
    }
  };

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // เรียก API getVaccineRequisitionSummary
  const fetchRequisitionReport = async () => {
    if (!filterData.FromWarehouseID) {
      alert("กรุณาเลือกคลังต้นทางก่อนค้นหารายงาน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineRequisitionSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          FromWarehouseID: filterData.FromWarehouseID || "",
          ToWarehouseID: filterData.ToWarehouseID || "",
          VaccineID: filterData.VaccineID || "",
          Status: filterData.Status || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.summary || [];
        setFlatData(data);
        // Group data by VaccineNameTH
        const grouped = {};
        data.forEach((item) => {
          const key = item.VaccineNameTH;
          const packageSize = Number(item.PackageSize) || 0;
          const requestQuantity = Number(item.RequestQuantity) || 0;
          const approvedQuantity = Number(item.ApprovedQuantity) || 0;
          const requestDoses =
            item.RequestDoses !== undefined && item.RequestDoses !== null
              ? Number(item.RequestDoses) || 0
              : requestQuantity * packageSize;
          const approvedDoses =
            item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
              ? Number(item.ApprovedDoses) || 0
              : approvedQuantity * packageSize;

          if (!grouped[key]) {
            grouped[key] = {
              VaccineNameTH: item.VaccineNameTH,
              VaccineNameEN: item.VaccineNameEN,
              LivestockType: item.LivestockType,
              PackageSize: packageSize,
              TotalRequestQuantity: 0,
              TotalRequestDoses: 0,
              TotalApprovedQuantity: 0,
              TotalApprovedDoses: 0,
              details: [],
            };
          }
          grouped[key].TotalRequestQuantity += requestQuantity;
          grouped[key].TotalRequestDoses += requestDoses;
          grouped[key].TotalApprovedQuantity += approvedQuantity;
          grouped[key].TotalApprovedDoses += approvedDoses;
          grouped[key].details.push({
            FromWarehouseName: item.FromWarehouseName,
            ToWarehouseName: item.ToWarehouseName,
            ReferenceDocumentNo: item.ReferenceDocumentNo,
            RequestQuantity: requestQuantity,
            RequestDoses: requestDoses,
            ApprovedQuantity: approvedQuantity,
            ApprovedDoses: approvedDoses,
            Status: getStatusText(item.Status),
            TransactionID: item.TransactionID,
          });
        });
        setRequisitionData(Object.values(grouped));
      } else {
        setFlatData([]);
        setRequisitionData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching requisition report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchFromWarehouseOptions();
      fetchVaccineOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "FromWarehouseID") {
      setFilterData({ ...filterData, [name]: value, ToWarehouseID: "" });
      if (value) {
        fetchToWarehouseOptions(value);
      } else {
        setToWarehouseOptions([]);
      }
    } else {
      setFilterData({ ...filterData, [name]: value });
    }
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ขอเบิก (ขวด)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestQuantity) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.TotalRequestQuantity || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่อนุมัติ (ขวด)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalApprovedQuantity) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.TotalApprovedQuantity || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่ขอเบิก (โดส)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalRequestDoses) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.TotalRequestDoses || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนที่อนุมัติ (โดส)</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalApprovedDoses) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.TotalApprovedDoses || 0).toLocaleString()}
        </Box>
      ),
    },
  ];

  const detailColumns = [
    {
      name: "คลังต้นทาง",
      selector: (row) => row.FromWarehouseName,
      sortable: true,
    },
    {
      name: "คลังปลายทาง",
      selector: (row) => row.ToWarehouseName,
      sortable: true,
    },
    {
      name: "เลขที่ใบเบิก",
      selector: (row) => row.TransactionID,
      sortable: true,
    },
    {
      name: "รอบการเบิก",
      selector: (row) => row.ReferenceDocumentNo,
      sortable: true,
    },
    {
      name: "จำนวนที่ขอเบิก (ขวด)",
      selector: (row) => Number(row.RequestQuantity) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.RequestQuantity || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: "จำนวนที่อนุมัติ (ขวด)",
      selector: (row) => Number(row.ApprovedQuantity) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.ApprovedQuantity || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: "จำนวนที่ขอเบิก (โดส)",
      selector: (row) => Number(row.RequestDoses) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.RequestDoses || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: "จำนวนที่อนุมัติ (โดส)",
      selector: (row) => Number(row.ApprovedDoses) || 0,
      sortable: true,
      right: true,
      cell: (row) => (
        <Box textAlign="right" w="100%">
          {Number(row.ApprovedDoses || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      name: "สถานะ",
      selector: (row) => row.Status,
      sortable: true,
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <Box p={4} bg="gray.50">
      <DataTable
        columns={detailColumns}
        data={data.details}
        customStyles={tableCustomStyles}
        dense
        noHeader
      />
    </Box>
  );

  const exportToExcel = () => {
    const wsData = flatData.map((item) => {
      const packageSize = Number(item.PackageSize) || 0;
      const requestDoses =
        item.RequestDoses !== undefined && item.RequestDoses !== null
          ? Number(item.RequestDoses) || 0
          : (Number(item.RequestQuantity) || 0) * packageSize;
      const approvedDoses =
        item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
          ? Number(item.ApprovedDoses) || 0
          : (Number(item.ApprovedQuantity) || 0) * packageSize;

      return {
        "ชื่อวัคซีน (TH)": item.VaccineNameTH,
        "ประเภทปศุสัตว์": item.LivestockType || "",
        "คลังต้นทาง": item.FromWarehouseName,
        "คลังปลายทาง": item.ToWarehouseName,
        "เลขที่ใบเบิก": item.TransactionID,
        "รอบการเบิก": item.ReferenceDocumentNo,
        "จำนวนขอเบิก (ขวด)": Number(item.RequestQuantity || 0),
        "จำนวนขอเบิก (โดส)": requestDoses,
        "จำนวนอนุมัติ (ขวด)": Number(item.ApprovedQuantity || 0),
        "จำนวนอนุมัติ (โดส)": approvedDoses,
        "สถานะ": getStatusText(item.Status),
      };
    });
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RequisitionReport");
    XLSX.writeFile(wb, "VaccineRequisitionReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("THSarabunNew.ttf", fontBase64);
      doc.addFont("THSarabunNew.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }
    
  let fromWarehouse = '';
  if (filterData.FromWarehouseID) {
    fromWarehouse = fromWarehouseOptions.find(
      (w) => w.WarehouseID === filterData.FromWarehouseID
    )?.WarehouseName || "ไม่ระบุ";
  }

    doc.setFontSize(16);
    doc.text(`รายงานการขอเบิกวัคซีน คลังต้นทาง : ${fromWarehouse}`, 14, 10);
    doc.setFontSize(10);
    doc.text(`ข้อมูล ณ วันที่: ${new Date().toLocaleDateString("th-TH")}`, 200, 10);

  let currentY = 18; // เริ่มต้นตำแหน่ง Y สำหรับแสดงเงื่อนไข
  // แสดงเงื่อนไขการค้นหา (เฉพาะที่มีค่า)
  if (filterData.StartDate || filterData.EndDate) {
    const startDate = filterData.StartDate
      ? new Date(filterData.StartDate).toLocaleDateString("th-TH")
      : "ไม่ระบุ";
    const endDate = filterData.EndDate
      ? new Date(filterData.EndDate).toLocaleDateString("th-TH")
      : "ไม่ระบุ";
    doc.text(`รายการใบเบิก ระหว่างวันที่: ${startDate} ถึง ${endDate}`, 14, currentY);
    currentY += 6; // ขยับลงสำหรับบรรทัดถัดไป
  }

  if (filterData.ToWarehouseID) {
    const toWarehouse = toWarehouseOptions.find(
      (w) => w.WarehouseID === filterData.ToWarehouseID
    )?.WarehouseName || "ไม่ระบุ";
    doc.text(`คลังวัคซีนที่ขอเบิก: ${toWarehouse}`, 14, currentY);
    currentY += 6;
  }

  if (filterData.VaccineID) {
    const vaccine = vaccineOptions.find(
      (v) => v.VaccineID === filterData.VaccineID
    )?.VaccineNameTH || "ไม่ระบุ";
    doc.text(`ชนิดวัคซีน: ${vaccine}`, 14, currentY);
    currentY += 6;
  }

  if (filterData.Status) {
    const status = statusOptions.find(
      (s) => s.value === filterData.Status
    )?.label || "ไม่ระบุ";
    doc.text(`สถานะการเบิก: ${status}`, 14, currentY);
    //currentY += 6;
  }

    autoTable(doc, {
      startY: currentY + 5,
      head: [
        [
          "ชื่อวัคซีน (TH)",
          "คลังปลายทาง",
          "เลขที่ใบเบิก",
          "รอบการเบิก",
          "จำนวนที่ขอเบิก (ขวด)",
          "จำนวนที่อนุมัติ (ขวด)",
          "จำนวนที่ขอเบิก (โดส)",
          "จำนวนที่อนุมัติ (โดส)",
          "สถานะ",
        ],
      ],
      body: flatData.map((item) => {
        const packageSize = Number(item.PackageSize) || 0;
        const requestQuantity = Number(item.RequestQuantity) || 0;
        const approvedQuantity = Number(item.ApprovedQuantity) || 0;
        const requestDoses =
          item.RequestDoses !== undefined && item.RequestDoses !== null
            ? Number(item.RequestDoses) || 0
            : requestQuantity * packageSize;
        const approvedDoses =
          item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
            ? Number(item.ApprovedDoses) || 0
            : approvedQuantity * packageSize;

        return [
          item.VaccineNameTH,
          item.ToWarehouseName,
          item.TransactionID,
          item.ReferenceDocumentNo,
          requestQuantity.toLocaleString(),
          approvedQuantity.toLocaleString(),
          requestDoses.toLocaleString(),
          approvedDoses.toLocaleString(),
          getStatusText(item.Status),
        ];
      }),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 11,
        cellPadding: 1,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },      
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fontSize: 11,
        halign: "center",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: "auto" },
        2: { cellWidth: "auto" },
        3: { cellWidth: "auto" },
        4: { cellWidth: "auto" },
        5: { cellWidth: "auto" },
        6: { cellWidth: "auto" },
        7: { cellWidth: "auto" },
        8: { cellWidth: "auto" },
      },
    });

    doc.save("VaccineRequisitionReport.pdf");
  };

  const printTable = () => {

    let conditionsHTML = '';
    let fromWarehouse = '';

    // เพิ่มเงื่อนไขการค้นหา (เฉพาะที่มีค่า)
    if (filterData.StartDate || filterData.EndDate) {
        const startDate = filterData.StartDate
        ? new Date(filterData.StartDate).toLocaleDateString("th-TH")
        : "ไม่ระบุ";
        const endDate = filterData.EndDate
        ? new Date(filterData.EndDate).toLocaleDateString("th-TH")
        : "ไม่ระบุ";
        conditionsHTML += `<p><strong>รายการใบเบิก ระหว่างวันที่:</strong> ${startDate} ถึง ${endDate}</p>`;
    }

    if (filterData.FromWarehouseID) {
        fromWarehouse = fromWarehouseOptions.find(
        (w) => w.WarehouseID === filterData.FromWarehouseID
        )?.WarehouseName || "ไม่ระบุ";
        //conditionsHTML += `<p><strong>คลังวัคซีนต้นทาง:</strong> ${fromWarehouse}</p>`;
    }

    if (filterData.ToWarehouseID) {
        const toWarehouse = toWarehouseOptions.find(
        (w) => w.WarehouseID === filterData.ToWarehouseID
        )?.WarehouseName || "ไม่ระบุ";
        conditionsHTML += `<p><strong>คลังวัคซีนที่ขอเบิก:</strong> ${toWarehouse}</p>`;
    }

    if (filterData.VaccineID) {
        const vaccine = vaccineOptions.find(
        (v) => v.VaccineID === filterData.VaccineID
        )?.VaccineNameTH || "ไม่ระบุ";
        conditionsHTML += `<p><strong>ชนิดวัคซีน:</strong> ${vaccine}</p>`;
    }

    if (filterData.Status) {
        const status = statusOptions.find(
        (s) => s.value === filterData.Status
        )?.label || "ไม่ระบุ";
        conditionsHTML += `<p><strong>สถานะการเบิก:</strong> ${status}</p>`;
    }
    const printContent = `
      <html>
        <head>
          <title>รายงานการขอเบิกวัคซีน</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>รายงานการขอเบิกวัคซีน จากคลังต้นทาง : ${fromWarehouse}</h2>
          <p><strong>ข้อมูล ณ วันที่:</strong> ${new Date().toLocaleDateString("th-TH")}</p>
          ${conditionsHTML}
          <table>
            <thead>
              <tr>
                <th>ชื่อวัคซีน (TH)</th>
                <th>คลังต้นทาง</th>
                <th>คลังปลายทาง</th>
                <th>เลขที่ใบเบิก</th>
                <th>รอบการเบิก</th>
                <th>จำนวนที่ขอเบิก (ขวด)</th>
                <th>จำนวนที่อนุมัติ (ขวด)</th>
                <th>จำนวนที่ขอเบิก (โดส)</th>
                <th>จำนวนที่อนุมัติ (โดส)</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              ${flatData
                .map((item) => {
                  const packageSize = Number(item.PackageSize) || 0;
                  const requestQuantity = Number(item.RequestQuantity) || 0;
                  const approvedQuantity = Number(item.ApprovedQuantity) || 0;
                  const requestDoses =
                    item.RequestDoses !== undefined && item.RequestDoses !== null
                      ? Number(item.RequestDoses) || 0
                      : requestQuantity * packageSize;
                  const approvedDoses =
                    item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
                      ? Number(item.ApprovedDoses) || 0
                      : approvedQuantity * packageSize;
                  return `
                    <tr>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.FromWarehouseName}</td>
                      <td>${item.ToWarehouseName}</td>
                      <td>${item.TransactionID}</td>
                      <td>${item.ReferenceDocumentNo}</td>
                      <td>${requestQuantity.toLocaleString()}</td>
                      <td>${approvedQuantity.toLocaleString()}</td>
                      <td>${requestDoses.toLocaleString()}</td>
                      <td>${approvedDoses.toLocaleString()}</td>
                      <td>${getStatusText(item.Status)}</td>
                    </tr>
                  `;
                })
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการขอเบิกวัคซีน" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการเบิก"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate}
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1" isRequired>
              <FormLabel fontSize="xl" color="gray">
                คลังต้นทาง
              </FormLabel>
              <Select
                name="FromWarehouseID"
                value={filterData.FromWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังต้นทาง"
                colorScheme="iBlue"
              >
                {fromWarehouseOptions.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                คลังที่ขอเบิก
              </FormLabel>
              <Select
                name="ToWarehouseID"
                value={filterData.ToWarehouseID}
                onChange={handleInputChange}
                placeholder="เลือกคลังที่ขอเบิก (ทั้งหมด)"
                colorScheme="iBlue"
                isDisabled={!filterData.FromWarehouseID}
              >
                {toWarehouseOptions.map((warehouse) => (
                  <option
                    key={warehouse.WarehouseID}
                    value={warehouse.WarehouseID}
                  >
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                สถานะ
              </FormLabel>
              <Select
                name="Status"
                value={filterData.Status}
                onChange={handleInputChange}
                placeholder="เลือกสถานะ (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    FromWarehouseID: fromWarehouseOptions.length === 1 ? fromWarehouseOptions[0].WarehouseID : "",
                    ToWarehouseID: "",
                    VaccineID: "",
                    Status: "",
                  })
                }
              />
              <Search onClick={fetchRequisitionReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {requisitionData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการการขอเบิกวัคซีน"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={requisitionData}
              pagination
              highlightOnHover
              striped
              responsive
              expandableRows
              expandableRowsComponent={ExpandedComponent}
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {flatData.length > 0 && (
        <VaccineRequisitionChart
          data={flatData}
          warehouseOptions={toWarehouseOptions}
        />
      )}
    </Box>
  );
};

export default VaccineRequisitionReport;